import { useEffect, useState, useRef } from "react";
import api, { formatApiError, downloadCoverLetterPdf, downloadCoverLetterDocx } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { AiDisclosure } from "@/components/AiDisclosure";
import AiAssistant from "@/components/AiAssistant";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Mail, Loader2, Sparkles, Download, FileText, Trash2, Plus, ExternalLink, ArrowLeft, Save,
} from "lucide-react";

const TONES = [
  { value: "personlig", label: "Personlig och varm" },
  { value: "formell", label: "Formell och professionell" },
  { value: "rak", label: "Rak och koncis" },
];
const TEMPLATES = [
  { value: "modern", label: "Modern (grön)" },
  { value: "klassisk", label: "Klassisk (svart)" },
  { value: "kompakt", label: "Kompakt (grön)" },
  { value: "grafisk", label: "Grafisk (blå)" },
  { value: "sidebar", label: "Sidebar (grön)" },
];

export default function PersonligtBrev() {
  const [list, setList] = useState(null);
  const [view, setView] = useState("list"); // list | create | edit
  const [form, setForm] = useState({ company: "", title: "", url: "", job_ad_text: "", tone: "personlig", application_id: null });
  const [generating, setGenerating] = useState(false);
  const [current, setCurrent] = useState(null); // active letter doc
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState("");
  const prefilled = useRef(false);

  const load = () => api.get("/cover-letters").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  // Förifyll från Skapa CV ("Skapa personligt brev")
  useEffect(() => {
    if (prefilled.current) return;
    try {
      const raw = localStorage.getItem("hamn_prefill_letter");
      if (raw) {
        const pf = JSON.parse(raw);
        setForm((f) => ({ ...f, ...pf }));
        setView("create");
        localStorage.removeItem("hamn_prefill_letter");
        prefilled.current = true;
      }
    } catch { /* ignore */ }
  }, []);

  const generate = async () => {
    if (!form.job_ad_text.trim() && !form.application_id) { toast.error("Klistra in jobbannonsen"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/cover-letters", form);
      setCurrent(data);
      setView("edit");
      toast.success("Personligt brev skapat");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const openLetter = async (id) => {
    try {
      const { data } = await api.get(`/cover-letters/${id}`);
      setCurrent(data);
      setView("edit");
    } catch { toast.error("Kunde inte öppna brevet"); }
  };

  const saveLetter = async () => {
    setSaving(true);
    try {
      await api.put(`/cover-letters/${current.id}`, { letter: current.letter, tone: current.tone, template: current.template });
      toast.success("Sparat");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeLetter = async (id) => {
    if (!window.confirm("Ta bort detta brev?")) return;
    await api.delete(`/cover-letters/${id}`).catch(() => {});
    if (current?.id === id) { setCurrent(null); setView("list"); }
    load();
  };

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const base = `HAMN_Brev_${[current.company, current.title].filter(Boolean).join("-").replace(/\s+/g, "_") || "Brev"}`;
      if (fmt === "pdf") await downloadCoverLetterPdf(current.id, `${base}.pdf`);
      else await downloadCoverLetterDocx(current.id, `${base}.docx`);
    } catch (e) { toast.error(e?.message || formatApiError(e.response?.data?.detail) || "Kunde inte ladda ner"); }
    finally { setDownloading(""); }
  };

  const setLetterField = (field, val) => setCurrent((c) => ({ ...c, letter: { ...c.letter, [field]: val } }));

  return (
    <div>
      <PageHeader
        title="Personligt brev"
        description="Skapa ett träffsäkert personligt brev till ett specifikt jobb – grundat på dina verkliga uppgifter och annonsen. Du väljer ton, redigerar fritt och exporterar till PDF eller Word."
      />
      <div className="flex justify-end -mt-2 mb-4">
        <AiAssistant
          triggerLabel="Fråga AI om hjälp"
          triggerTestId="brev-ai-trigger"
          title="AI-hjälp med ditt personliga brev"
          intro="Jag hjälper dig hitta rätt ton och formuleringar – grundat på dina verkliga uppgifter och jobbet."
          suggestions={[
            "Hur inleder jag brevet starkt?",
            "Hjälp mig koppla min erfarenhet till det här jobbet",
            "Gör min avslutning mer övertygande",
          ]}
          loadHistory={async () => (await api.get("/assistant/advice/personligt_brev")).data}
          send={async (message) => (await api.post("/assistant/advice", {
            topic: "personligt_brev",
            message,
            context: current
              ? { företag: current.company, tjänst: current.title, nuvarande_brev: current.letter }
              : { företag: form.company, tjänst: form.title, jobbannons: (form.job_ad_text || "").slice(0, 2000) },
          })).data}
        />
      </div>

      {/* LISTA */}
      {view === "list" && (
        <>
          <div className="flex justify-end mb-4">
            <Button className="rounded-full" onClick={() => { setForm({ company: "", title: "", url: "", job_ad_text: "", tone: "personlig", application_id: null }); setView("create"); }} data-testid="new-cover-letter-btn">
              <Plus className="h-4 w-4 mr-2" /> Nytt personligt brev
            </Button>
          </div>
          {list === null ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary" /></div>
          ) : list.length === 0 ? (
            <Card className="rounded-2xl border-dashed border-border p-10 text-center" data-testid="cover-letters-empty">
              <Mail className="h-8 w-8 text-primary mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Inga personliga brev ännu. Skapa ditt första till ett jobb du vill söka.</p>
            </Card>
          ) : (
            <div className="grid gap-3" data-testid="cover-letters-list">
              {list.map((l) => (
                <Card key={l.id} className="rounded-2xl border-border p-4 flex items-center justify-between gap-3" data-testid={`cover-letter-${l.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0"><Mail className="h-5 w-5" /></div>
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{[l.title, l.company].filter(Boolean).join(" – ") || "Personligt brev"}</div>
                      <div className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString("sv-SE")}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {l.url && (
                      <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`apply-cover-letter-${l.id}`}>
                        <Button size="sm" variant="outline" className="rounded-full min-h-[44px] border-primary/40 text-primary hover:bg-primary/5"><ExternalLink className="h-4 w-4 mr-1.5" /> Ansök</Button>
                      </a>
                    )}
                    <Button size="sm" variant="outline" className="rounded-full min-h-[44px]" onClick={() => openLetter(l.id)} data-testid={`open-cover-letter-${l.id}`}>Öppna</Button>
                    <Button size="sm" variant="ghost" className="rounded-full min-h-[44px] text-muted-foreground" onClick={() => removeLetter(l.id)} data-testid={`delete-cover-letter-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* SKAPA */}
      {view === "create" && (
        <Card className="rounded-2xl border-border p-5 md:p-6" data-testid="cover-letter-create">
          <button onClick={() => setView("list")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"><ArrowLeft className="h-4 w-4" /> Tillbaka</button>
          {form.application_id && (
            <div className="rounded-xl border border-primary/25 bg-primary/[0.05] p-3.5 text-sm text-muted-foreground mb-4" data-testid="cl-prefilled-note">
              Jobbet är hämtat från ditt CV{[form.title, form.company].filter(Boolean).length ? ` (${[form.title, form.company].filter(Boolean).join(" – ")})` : ""}. Välj ton nedan och skapa brevet – du kan även klistra in annonstext om du vill komplettera.
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-sm">Företag</Label>
              <Input className="mt-1.5 h-11" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="T.ex. ICA Maxi" data-testid="cl-company" />
            </div>
            <div>
              <Label className="text-sm">Tjänst / titel</Label>
              <Input className="mt-1.5 h-11" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="T.ex. Butikssäljare" data-testid="cl-title" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm">Länk till annonsen (valfritt)</Label>
              <Input className="mt-1.5 h-11" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="cl-url" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm">Jobbannonsen</Label>
              <Textarea className="mt-1.5" rows={7} value={form.job_ad_text} onChange={(e) => setForm({ ...form, job_ad_text: e.target.value })} placeholder="Klistra in hela jobbannonsen här…" data-testid="cl-jobad" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm">Ton</Label>
              <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="cl-tone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-5">
            <Button onClick={generate} disabled={generating} className="rounded-full" data-testid="generate-cover-letter-btn">
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Skapa personligt brev
            </Button>
          </div>
        </Card>
      )}

      {/* REDIGERA */}
      {view === "edit" && current && (
        <div className="space-y-5" data-testid="cover-letter-edit">
          <button onClick={() => setView("list")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Alla brev</button>
          <AiDisclosure text={<><strong className="text-foreground">AI-genererat personligt brev.</strong> Skapat utifrån dina uppgifter och annonsen. Granska och redigera – du bestämmer slutresultatet. Exporten märks som AI-genererad (EU:s AI-förordning, art. 50).</>} />
          <Card className="rounded-2xl border-border p-5 md:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="text-sm text-muted-foreground">{[current.title, current.company].filter(Boolean).join(" – ")}</div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Mall / färg</Label>
                <Select value={current.template || "modern"} onValueChange={(v) => { setCurrent((c) => ({ ...c, template: v })); api.put(`/cover-letters/${current.id}`, { template: v }).catch(() => {}); }}>
                  <SelectTrigger className="h-10 w-44" data-testid="cl-edit-template"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TEMPLATES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Hälsningsfras</Label>
                <Input className="mt-1.5 h-11" value={current.letter?.greeting || ""} onChange={(e) => setLetterField("greeting", e.target.value)} data-testid="cl-edit-greeting" />
              </div>
              <div>
                <Label className="text-sm">Brevtext</Label>
                <Textarea className="mt-1.5" rows={14} value={current.letter?.body || ""} onChange={(e) => setLetterField("body", e.target.value)} data-testid="cl-edit-body" />
              </div>
              <div>
                <Label className="text-sm">Avslutning</Label>
                <Input className="mt-1.5 h-11" value={current.letter?.signoff || ""} onChange={(e) => setLetterField("signoff", e.target.value)} data-testid="cl-edit-signoff" />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-border">
              <Button onClick={saveLetter} disabled={saving} className="rounded-full" data-testid="cl-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Spara
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => download("pdf")} disabled={downloading === "pdf"} data-testid="cl-download-pdf">
                {downloading === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} PDF
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => download("docx")} disabled={downloading === "docx"} data-testid="cl-download-docx">
                {downloading === "docx" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />} Word
              </Button>
              {current.url && (
                <a href={current.url} target="_blank" rel="noopener noreferrer" className="ml-auto">
                  <Button className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="cl-apply-link">
                    Ansök här <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>
                </a>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
